import { NOTE_TAG_COLORS } from './AnalyticsConstants';

export function parseNotizen(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    const t = typeof raw === 'string' ? raw.trim() : '';
    return t ? [{ id: `legacy_${Date.now()}`, text: t, erstellt_am: null }] : [];
  }
}

export function serializeNotizen(arr) { return JSON.stringify(arr); }

export function newNote(text) {
  return { id: `n_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, text, erstellt_am: new Date().toISOString() };
}

export function formatNoteDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function getNoteTag(text) {
  if (!text) return null;
  const upper = text.trim().toUpperCase();
  for (const tag of Object.keys(NOTE_TAG_COLORS)) {
    if (upper.startsWith(tag + ':') || upper.startsWith(tag + ' ') || upper === tag) {
      return tag;
    }
  }
  return null;
}

export function pct(n, total) {
  if (!total) return '0%';
  return `${Math.round((n / total) * 100)}%`;
}

export function formatGehalt(raw) {
  const t = raw.trim();
  if (!t || t.includes('€') || /[a-zA-Z]/.test(t)) return t;
  const digits = t.replace(/[.,\s]/g, '');
  if (!/^\d+$/.test(digits)) return t;
  return parseInt(digits, 10).toLocaleString('de-DE', { maximumFractionDigits: 0 }) + ' €';
}

// ─── Dashboard data helpers ────────────────────────────────────────────────

function getISOWeek(d) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

// Returns array of { label, sent, interview, zusage } for the last numWeeks weeks
export function computeWeeklyBuckets(stellen = [], numWeeks = 12) {
  const now = new Date();
  const buckets = [];
  for (let i = numWeeks - 1; i >= 0; i--) {
    const start = new Date(now);
    const dayOfWeek = now.getDay();
    const daysToMonday = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek);
    start.setDate(now.getDate() + daysToMonday - i * 7);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 7);
    const kw = `KW${String(getISOWeek(start)).padStart(2, '0')}`;
    buckets.push({ label: kw, start, end, sent: 0, interview: 0, zusage: 0 });
  }
  stellen.forEach(s => {
    if (!s.erstellt_am) return;
    const d = new Date(s.erstellt_am);
    const bucket = buckets.find(b => d >= b.start && d < b.end);
    if (!bucket) return;
    const st = s.bewerbungs_status || '';
    if (st === 'zusage') bucket.zusage++;
    else if (st === 'kennenlerngespraech') bucket.interview++;
    else if (st === 'abgeschickt') bucket.sent++;
  });
  return buckets.map(({ label, sent, interview, zusage }) => ({ label, sent, interview, zusage }));
}

// Returns { sent, interview, zusage, absage } counts for funnel
export function computeFunnel(stellen = []) {
  let sent = 0, interview = 0, zusage = 0, absage = 0;
  stellen.forEach(s => {
    const st = s.bewerbungs_status || '';
    if (['abgeschickt', 'kennenlerngespraech', 'zusage', 'absage'].includes(st)) sent++;
    if (['kennenlerngespraech', 'zusage'].includes(st)) interview++;
    if (st === 'zusage') zusage++;
    if (st === 'absage') absage++;
  });
  return { sent, interview, zusage, absage };
}

function formatRelativeTime(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (h < 1) return 'gerade eben';
  if (h < 24) return `vor ${h} Std.`;
  if (d === 1) return 'gestern';
  if (d < 7) return `vor ${d} Tagen`;
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

// Returns last 8 activity items derived from stellen sorted by erstellt_am desc
export function computeActivityFeed(stellen = []) {
  const STATUS_LABEL = {
    abgeschickt: 'Bewerbung abgeschickt',
    kennenlerngespraech: 'Interview geplant',
    zusage: 'Zusage erhalten!',
    absage: 'Absage erhalten',
    unterlagen_angefertigt: 'Unterlagen angefertigt',
  };
  const STATUS_KIND = {
    abgeschickt: 'sent',
    kennenlerngespraech: 'int',
    zusage: 'offer',
    absage: 'reject',
    unterlagen_angefertigt: 'draft',
  };
  return [...stellen]
    .filter(s => s.bewerbungs_status && s.bewerbungs_status !== '')
    .sort((a, b) => new Date(b.erstellt_am || 0) - new Date(a.erstellt_am || 0))
    .slice(0, 8)
    .map(s => ({
      kind: STATUS_KIND[s.bewerbungs_status] || 'sent',
      company: s.empfaenger_unternehmen || s.name || 'Unbenannt',
      text: STATUS_LABEL[s.bewerbungs_status] || s.bewerbungs_status,
      time: formatRelativeTime(s.erstellt_am),
    }));
}

const DOMAIN_MAP = {
  'linkedin.com': 'LinkedIn', 'xing.com': 'XING',
  'stepstone.de': 'StepStone', 'stepstone.at': 'StepStone',
  'indeed.com': 'Indeed', 'indeed.de': 'Indeed',
  'monster.de': 'Monster', 'monster.com': 'Monster',
  'karriere.at': 'karriere.at', 'stellenanzeigen.de': 'Stellenanzeigen.de',
  'glassdoor.com': 'Glassdoor', 'jobs.google.com': 'Google Jobs',
  'arbeitsagentur.de': 'Bundesagentur für Arbeit',
};

function plattformAusUrl(url) {
  if (!url) return null;
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    for (const [domain, name] of Object.entries(DOMAIN_MAP)) {
      if (host === domain || host.endsWith('.' + domain)) return name;
    }
    return host || null;
  } catch {
    return null;
  }
}

export function plattformAusStelle(s) {
  if (s.plattform) return s.plattform;
  if (s.stellen_struktur?.plattform) return s.stellen_struktur.plattform;
  const fromUrl = plattformAusUrl(s.url);
  if (fromUrl) return fromUrl;
  return 'Direkt';
}

// Returns [{ name, value }] grouped by actual platform
export function computeSources(stellen = []) {
  const counts = {};
  for (const s of stellen) {
    const p = plattformAusStelle(s);
    counts[p] = (counts[p] || 0) + 1;
  }
  return Object.entries(counts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

// Returns array of 12 weekly totals for sparkline
export function computeSparkline(stellen = [], numWeeks = 12) {
  return computeWeeklyBuckets(stellen, numWeeks).map(b => b.sent + b.interview + b.zusage);
}

export function trendVsPrev(stellen = [], filterFn) {
  const MS4W = 28 * 86400000;
  const now = Date.now();
  const curr = stellen.filter(s => {
    const d = new Date(s.erstellt_am || 0).getTime();
    return filterFn(s) && d > now - MS4W;
  }).length;
  const prev = stellen.filter(s => {
    const d = new Date(s.erstellt_am || 0).getTime();
    return filterFn(s) && d <= now - MS4W && d > now - 2 * MS4W;
  }).length;
  if (prev === 0) return curr > 0 ? 100 : 0;
  return Math.round(((curr - prev) / prev) * 100);
}
